import { meetsThreshold } from './levels.js';
import { MAX_EVENT_LENGTH, MAX_MESSAGE_LENGTH, normalizeUser, serializeError, truncate, } from './record.js';
import { createRedactor } from './redact.js';
import { scrubSecretsIn } from './scrub.js';
const noop = () => { };
/**
 * Build a logger.
 *
 * Deliberately free of Node: no `process`, no `AsyncLocalStorage`, no AWS SDK.
 * This module is reachable from a browser bundle through
 * `@speles7172/log-client/core`, and one Node-only import here would drag the
 * whole SDK into a frontend build. That has already happened once in this
 * repository — `sql-console` 0.3.0 — and blanked a consumer's page.
 */
export function createLogger(options) {
    const { source, sink, level = 'info', environment, origin, ambientContext, clock = () => new Date(), onError = noop, } = options;
    const scrubText = options.scrubText === false ? undefined : (options.scrubText ?? ((text) => scrubSecretsIn(text)));
    // The default redactor is built here rather than shared, so that `scrubText`
    // reaches string values inside `fields` too — one policy for free text,
    // wherever it turns up. A caller supplying their own redactor owns that
    // decision for fields; `scrubText` still applies to the message and the error.
    const redact = options.redact === false
        ? (value) => value
        : (options.redact ??
            createRedactor(scrubText === undefined ? {} : { scrubStrings: scrubText }));
    // Every promise a sink has handed back and not yet settled. `flush()` waits
    // on these: a sink that writes asynchronously is otherwise "flushed" while
    // its last batch is still in flight, which in Lambda means frozen mid-write.
    const pending = new Set();
    function deliver(record) {
        let result;
        try {
            result = sink.write([record]);
        }
        catch (error) {
            onError(error);
            return;
        }
        if (result === undefined || typeof result.then !== 'function')
            return;
        const promise = Promise.resolve(result)
            .catch(onError)
            .finally(() => {
            pending.delete(promise);
        });
        pending.add(promise);
    }
    const scrub = (text) => (scrubText === undefined ? text : scrubText(text));
    /**
     * An error's own free text, scrubbed — including down the cause chain.
     *
     * `serializeError` produces this outside the field redactor's reach, and
     * routing it through that redactor would accomplish nothing: not one key in an
     * error object matches a name on the deny list. The leak here is in the values,
     * so the fix has to be too.
     */
    function scrubError(error) {
        if (scrubText === undefined)
            return error;
        return {
            ...error,
            message: scrub(error.message),
            ...(error.stack === undefined ? {} : { stack: scrub(error.stack) }),
            ...(error.cause === undefined ? {} : { cause: scrubError(error.cause) }),
        };
    }
    function build(entry, context) {
        if (!meetsThreshold(entry.level, level))
            return undefined;
        const ambient = ambientContext?.();
        const fields = mergeFields(ambient?.fields, context.fields, entry.fields);
        const user = normalizeUser(entry.user ?? context.user ?? ambient?.user);
        const requestId = entry.requestId ?? context.requestId ?? ambient?.requestId;
        const sessionId = entry.sessionId ?? context.sessionId ?? ambient?.sessionId;
        const traceId = entry.traceId ?? context.traceId ?? ambient?.traceId;
        const redacted = fields === undefined ? undefined : redact(fields);
        return {
            timestamp: toIso(entry.timestamp, clock),
            level: entry.level,
            event: truncate(entry.event, MAX_EVENT_LENGTH),
            ...(entry.message === undefined
                ? {}
                : { message: truncate(scrub(entry.message), MAX_MESSAGE_LENGTH) }),
            source,
            ...(environment === undefined ? {} : { environment }),
            ...(origin === undefined ? {} : { origin }),
            ...(user === undefined ? {} : { user }),
            ...(requestId === undefined ? {} : { requestId }),
            ...(sessionId === undefined ? {} : { sessionId }),
            ...(traceId === undefined ? {} : { traceId }),
            ...(entry.durationMs === undefined ? {} : { durationMs: entry.durationMs }),
            ...(entry.error === undefined ? {} : { error: scrubError(serializeError(entry.error)) }),
            ...(redacted === undefined || Object.keys(redacted).length === 0 ? {} : { fields: redacted }),
        };
    }
    function make(context) {
        function write(entry) {
            // A logging call is not a place to throw. Anything unexpected in here — a
            // getter that explodes during redaction, a structure a custom redactor
            // cannot handle — is reported and dropped.
            try {
                const record = build(entry, context);
                if (record !== undefined)
                    deliver(record);
            }
            catch (error) {
                onError(error);
            }
        }
        const at = (entryLevel) => (event, detail, fields) => {
            write({
                level: entryLevel,
                event,
                ...(typeof detail === 'string' ? { message: detail } : {}),
                ...(detail !== undefined && typeof detail !== 'string'
                    ? { error: detail, message: detail.message }
                    : {}),
                ...(fields === undefined ? {} : { fields }),
            });
        };
        return {
            level,
            debug: at('debug'),
            info: at('info'),
            warn: at('warn'),
            error: at('error'),
            event: (name, fields) => write({ level: 'info', event: name, ...(fields === undefined ? {} : { fields }) }),
            log: write,
            child: (extra) => make(mergeContext(context, extra)),
            flush: async () => {
                try {
                    // Two rounds: the first settles writes already in flight, and a sink
                    // whose own `flush` starts another write is settled by the second.
                    await Promise.all([...pending]);
                    await sink.flush?.();
                    await Promise.all([...pending]);
                }
                catch (error) {
                    onError(error);
                }
            },
        };
    }
    return make(options.context ?? {});
}
function mergeContext(base, extra) {
    const fields = mergeFields(base.fields, extra.fields);
    return {
        ...base,
        ...extra,
        ...(fields === undefined ? {} : { fields }),
    };
}
/**
 * Shallow on purpose.
 *
 * A deep merge would make a child carrying `fields.request.id` and a later call
 * carrying `fields.request.path` silently combine into a record that neither
 * call describes. Last writer wins per key, and nesting is the caller's to
 * arrange.
 */
function mergeFields(...sources) {
    const present = sources.filter((source) => source !== undefined);
    if (present.length === 0)
        return undefined;
    return Object.assign({}, ...present);
}
function toIso(timestamp, clock) {
    if (timestamp === undefined)
        return clock().toISOString();
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    // An unparseable timestamp from a browser must not produce "Invalid Date",
    // which Insights cannot sort and the console cannot render.
    return Number.isNaN(date.getTime()) ? clock().toISOString() : date.toISOString();
}
/** A sink that keeps everything in memory. For tests, and for nothing else. */
export function createMemorySink() {
    const records = [];
    return {
        records,
        write(batch) {
            records.push(...batch);
        },
        clear() {
            records.length = 0;
        },
    };
}
//# sourceMappingURL=logger.js.map